import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, MessageCircle, Send, Trash2, Image as ImageIcon } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString("de-DE");
}

function Composer({ onPosted, parentId = null, placeholder = "Was läuft, Athlet?" }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [showImg, setShowImg] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/social/posts", { content: text.trim(), image_url: image || null, parent_id: parentId });
      setText("");
      setImage("");
      setShowImg(false);
      onPosted?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="card-solid p-4 space-y-3" data-testid="post-composer">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={parentId ? 2 : 3}
        className="input resize-none"
        maxLength={2000}
        data-testid="post-input"
      />
      {showImg && (
        <input
          type="url"
          placeholder="Bild-URL (optional)"
          value={image}
          onChange={(e) => setImage(e.target.value)}
          className="input"
          data-testid="post-image-input"
        />
      )}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setShowImg((s) => !s)}
          className="btn-ghost"
          data-testid="post-toggle-image"
        >
          <ImageIcon className="w-4 h-4" /> Bild
        </button>
        <button type="submit" disabled={busy || !text.trim()} className="btn-primary" data-testid="post-submit">
          <Send className="w-4 h-4" /> Posten
        </button>
      </div>
    </form>
  );
}

function Post({ p, onChange, currentUserId }) {
  const [showReply, setShowReply] = useState(false);

  const toggleLike = async () => {
    await api.post(`/social/posts/${p.id}/like`);
    onChange?.();
  };
  const remove = async () => {
    if (!window.confirm("Post löschen?")) return;
    await api.delete(`/social/posts/${p.id}`);
    onChange?.();
  };

  return (
    <article className="card-solid p-4 space-y-3" data-testid={`post-${p.id}`}>
      <div className="flex items-center gap-3">
        {p.author_picture ? (
          <img src={p.author_picture} alt="" className="w-9 h-9 rounded-sm object-cover border border-white/10" />
        ) : (
          <div className="w-9 h-9 bg-neon-gradient-soft border border-primary/30 text-primary flex items-center justify-center font-mono text-xs rounded-sm">
            {p.author_name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <div className="font-display text-base tracking-tight">{p.author_name}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{timeAgo(p.created_at)}</div>
        </div>
        {(p.author_id === currentUserId) && (
          <button onClick={remove} className="btn-ghost text-red-400 !p-1.5" data-testid={`delete-post-${p.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="text-white/90 whitespace-pre-wrap text-sm leading-relaxed">{p.content}</div>
      {p.image_url && <img src={p.image_url} alt="" className="rounded-sm border border-white/10 max-h-96 w-full object-cover" />}
      <div className="flex items-center gap-4 pt-2 border-t border-white/5">
        <button
          onClick={toggleLike}
          data-testid={`like-${p.id}`}
          className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] ${
            p.liked_by_me ? "text-magenta" : "text-white/50 hover:text-magenta"
          } transition-colors`}
        >
          <Heart className="w-3.5 h-3.5" fill={p.liked_by_me ? "currentColor" : "none"} />
          {p.likes}
        </button>
        <button
          onClick={() => setShowReply((s) => !s)}
          data-testid={`reply-toggle-${p.id}`}
          className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-white/50 hover:text-primary transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" /> {p.comments}
        </button>
        <Link to={`/social/${p.id}`} className="ml-auto text-xs font-mono uppercase tracking-[0.2em] text-white/40 hover:text-primary link-underline">
          Thread →
        </Link>
      </div>
      {showReply && <Composer onPosted={() => { setShowReply(false); onChange?.(); }} parentId={p.id} placeholder="Antworten…" />}
    </article>
  );
}

export default function Social() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [scope, setScope] = useState("all");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/social/feed", { params: { scope } });
      setPosts(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6 fade-in" data-testid="social-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Social</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Vereins-Feed</h1>
        <p className="text-white/60 text-sm mt-2">Posts, Updates und Diskussionen rund um Nordgaming.</p>
      </div>

      <div className="flex gap-2">
        {[
          { k: "all", l: "Alle" },
          { k: "friends", l: "Freunde" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setScope(f.k)}
            data-testid={`feed-scope-${f.k}`}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-[0.2em] font-mono border transition-colors ${
              scope === f.k ? "bg-primary/10 border-primary text-primary" : "border-border text-white/60 hover:border-primary/40"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <Composer onPosted={load} />

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      <div className="space-y-4">
        {posts.length === 0 ? (
          <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
            Noch keine Posts. Sei der erste!
          </div>
        ) : (
          posts.map((p) => <Post key={p.id} p={p} onChange={load} currentUserId={user?.user_id} />)
        )}
      </div>
    </div>
  );
}

export function SocialThread() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [params] = [window.location.pathname.split("/").pop()];
  const id = params;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/social/posts/${id}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-red-400 text-sm font-mono">{error}</div>;
  if (!data) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  return (
    <div className="space-y-4 fade-in" data-testid="social-thread-page">
      <Link to="/social" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-primary link-underline">← Feed</Link>
      <Post p={data.post} onChange={load} currentUserId={user?.user_id} />
      <Composer onPosted={load} parentId={data.post.id} placeholder="Antworten…" />
      <div className="space-y-3 pl-6 border-l-2 border-white/10">
        {data.comments.map((c) => <Post key={c.id} p={c} onChange={load} currentUserId={user?.user_id} />)}
      </div>
    </div>
  );
}
